import { all, put, takeEvery, fork, select, call } from "redux-saga/effects";
import moment from "moment";
import axios from "axios";
import { apiURL } from "../actions/helpers";

// Action Types
export const MULTI_SPARKLINE_DATA_LOAD = "MULTI_SPD/LOAD";
export const MULTI_SPARKLINE_DATA_LOAD_SUCCESS = "MULTI_SPD/LOAD/SUCCESS";
export const MULTI_SPARKLINE_DATA_LOAD_FAILURE = "MULTI_SPD/LOAD/FAILURE";

// Cache expiry time in milliseconds (1hr 30 minutes)
const CACHE_EXPIRY_TIME = 130 * 60 * 1000;

// Action Creators
export const multiSparklineDataLoad = (
  symbols,
  resolution = "1",
  forceRefresh = false,
  timeframe = "YTD",
) => ({
  type: MULTI_SPARKLINE_DATA_LOAD,
  symbols,
  resolution,
  forceRefresh,
  timeframe,
});

export const multiSparklineDataLoadSuccess = (
  data,
  symbol,
  loading = false,
  timeframe = "YTD",
) => ({
  type: MULTI_SPARKLINE_DATA_LOAD_SUCCESS,
  data,
  symbol,
  loading,
  timeframe,
  timestamp: Date.now(),
});

export const multiSparklineDataLoadFailure = (error, symbol) => ({
  type: MULTI_SPARKLINE_DATA_LOAD_FAILURE,
  error,
  symbol,
});

// Selector to get the current state
const getMultiSparklineState = (state) => state.multiSparklineData;

// Function to make a single API request for multiple symbols
function* fetchMultipleSymbolsData(symbols, resolution, timeframe) {
  try {
    // Calculate time range based on timeframe
    let fromTime, toTime, timespan;
    toTime = moment().valueOf();

    switch (timeframe) {
      case "1D":
        fromTime = moment().subtract(1, "days").valueOf();
        timespan = "h";
        break;
      case "7D":
        fromTime = moment().subtract(7, "days").valueOf();
        timespan = "D";
        break;
      case "1M":
        fromTime = moment().subtract(1, "months").valueOf();
        timespan = "D";
        break;
      case "1Y":
        fromTime = moment().subtract(1, "years").valueOf();
        timespan = "W";
        break;
      case "YTD":
      default:
        fromTime = moment().startOf("year").valueOf();
        timespan = "D";
        break;
    }

    // Format the symbols for the API request
    const formattedSymbols = symbols
      .map((symbol) => `${symbol}{=${resolution}${timespan}}`)
      .join(",");

    // Make a single API request for all symbols
    const url = `${apiURL}/company/events/json?events=Candle&symbols=${encodeURIComponent(formattedSymbols)}&fromTime=${fromTime}&toTime=${toTime}&indent`;
    const { data } = yield call(axios.get, url, {
      withCredentials: true,
    });

    if (!data || !data.Candle) {
      throw new Error("Invalid response from API");
    }

    // Process the data for each symbol
    const results = [];
    for (const symbol of symbols) {
      const formattedSymbol = `${symbol}{=${resolution}${timespan}}`;
      const candles = data.Candle[formattedSymbol];

      if (candles && candles.length > 0) {
        results.push({
          symbol,
          data: candles.map((item) => item.close.toFixed(2)),
          success: true,
          timeframe,
        });
      } else {
        results.push({
          symbol,
          error: new Error(`No data found for ${symbol}`),
          success: false,
        });
      }
    }

    return results;
  } catch (error) {
    console.log({ error }, `Error loading sparkline data for multiple symbols`);
    // If the batch request fails, return error for all symbols
    return symbols.map((symbol) => ({
      symbol,
      error,
      success: false,
    }));
  }
}

// Sagas
function* fetchMultiSparklineData(action) {
  const { symbols, resolution, forceRefresh, timeframe } = action;
  const currentState = yield select(getMultiSparklineState);
  const currentTime = Date.now();

  // Set initial loading state for all symbols that need fetching
  const symbolsToFetch = [];

  for (const symbol of symbols) {
    // Check if we already have valid data for this symbol
    const cachedData = currentState.sparklineData[symbol];
    const cachedTimestamp = currentState.timestamps?.[symbol];
    const cachedTimeframe = currentState.timeframes?.[symbol];
    const isCacheValid =
      !forceRefresh &&
      cachedData &&
      cachedData.length > 0 &&
      cachedTimestamp &&
      cachedTimeframe === timeframe &&
      currentTime - cachedTimestamp < CACHE_EXPIRY_TIME;

    // Skip fetching if we have valid cached data
    if (isCacheValid) {
      continue;
    }

    // Set loading state
    yield put(multiSparklineDataLoadSuccess([], symbol, true, timeframe));
    symbolsToFetch.push(symbol);
  }

  if (symbolsToFetch.length > 0) {
    // If we have symbols to fetch, make a single batch request
    const results = yield call(
      fetchMultipleSymbolsData,
      symbolsToFetch,
      resolution,
      timeframe,
    );

    // Process results
    for (const result of results) {
      if (result.success) {
        yield put(
          multiSparklineDataLoadSuccess(
            result.data,
            result.symbol,
            false,
            result.timeframe,
          ),
        );
      } else {
        yield put(multiSparklineDataLoadFailure(result.error, result.symbol));
      }
    }
  }
}

function* listenMultiSparklineDataLoad() {
  yield takeEvery(MULTI_SPARKLINE_DATA_LOAD, fetchMultiSparklineData);
}

// Root Saga
export function* saga() {
  yield all([fork(listenMultiSparklineDataLoad)]);
}

const INIT_STATE = {
  sparklineData: {},
  loading: {},
  timestamps: {},
  timeframes: {},
};

// Reducer
const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case MULTI_SPARKLINE_DATA_LOAD_SUCCESS:
      return {
        ...state,
        sparklineData: {
          ...state.sparklineData,
          [action.symbol]: action.data,
        },
        loading: {
          ...state.loading,
          [action.symbol]: action.loading,
        },
        timestamps: {
          ...state.timestamps,
          [action.symbol]: action.loading
            ? state.timestamps?.[action.symbol]
            : action.timestamp,
        },
        timeframes: {
          ...state.timeframes,
          [action.symbol]: action.timeframe,
        },
      };
    case MULTI_SPARKLINE_DATA_LOAD_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.symbol]: false,
        },
      };
    default:
      return state;
  }
};

export default reducer;
